<!-- TelcoTrack — unified materials panel (polygon / user scopes) -->
<div class="materials-panel">
  <div ttPageHeaderFilters class="flex flex-wrap items-end gap-3">
    <details appDetailsCloseOnOutsideClick class="tt-filter-search-disclosure tt-filter-search-disclosure--lg min-w-0">
      <summary aria-label="Otevřít vyhledávání">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
        </svg>
      </summary>
      <label [attr.for]="'mp-search-' + scope" class="tt-filter-label">{{ isUserScope ? 'Materiál, SAP, dodavatel' : 'Materiál, SAP, uživatel, dodavatel' }}</label>
      <input
        [id]="'mp-search-' + scope"
        type="text"
        [(ngModel)]="searchQuery"
        (ngModelChange)="onSearchQueryChange()"
        [placeholder]="isUserScope ? 'Materiál, SAP, dodavatel…' : 'Materiál, SAP, uživatel, dodavatel…'"
        class="tt-filter-control"
      />
    </details>
    <app-filter-date-range
      [idPrefix]="'mp-' + scope"
      [dateFrom]="dateFrom"
      [dateTo]="dateTo"
      (dateFromChange)="dateFrom = $event"
      (dateToChange)="dateTo = $event"
      (rangeBlur)="onDateFieldBlur()"
    ></app-filter-date-range>
    <app-filter-multi-select
      *ngIf="showUserFilter"
      label="Uživatel"
      icon="users"
      [options]="userFilterOptions"
      [selected]="filterUserIds"
      emptyLabel="Všichni uživatelé"
      controlClass="w-52"
      (selectedChange)="onFilterUserIdsChange($event)"
    ></app-filter-multi-select>
    <app-filter-multi-select
      *ngIf="showPolygonFilter"
      label="Polygon"
      icon="polygon"
      [options]="polygonFilterOptions"
      [selected]="filterPolygonIds"
      emptyLabel="Všechny polygony"
      controlClass="w-52"
      (selectedChange)="onPolygonFilterChange($event)"
    ></app-filter-multi-select>
    <app-filter-multi-select
      *ngIf="showObjectTypeFilter"
      label="Typ objektu"
      icon="objects"
      [options]="objectTypeFilterOptions"
      [selected]="filterObjectTypes"
      emptyLabel="Všechny typy"
      controlClass="min-w-[10rem]"
      (selectedChange)="onObjectTypeFilterChange($event)"
    ></app-filter-multi-select>
    <app-filter-multi-select
      *ngIf="showPhaseFilter"
      label="Fáze"
      [options]="phaseFilterOptions"
      [selected]="filterObjectPhases"
      emptyLabel="Fáze"
      controlClass="min-w-[5rem] max-w-[9rem]"
      (selectedChange)="onObjectPhaseFilterChange($event)"
    ></app-filter-multi-select>
    <div class="flex flex-wrap items-center gap-2 self-end">
      <app-button [variant]="displayMode === 'detail' ? 'primary' : 'secondary'" size="md" (onClick)="setDisplayMode('detail')">Detailní</app-button>
      <app-button [variant]="displayMode === 'aggregated' ? 'primary' : 'secondary'" size="md" (onClick)="setDisplayMode('aggregated')">Souhrn</app-button>
    </div>
    <app-button
      *ngIf="showClearFiltersButton && hasAnyFilter"
      variant="secondary"
      size="md"
      (onClick)="clearAllFilters()">
      Zrušit filtry
    </app-button>
  </div>

  <app-data-table
    *ngIf="displayMode === 'detail'"
    [columns]="materialTableColumns"
    [rows]="paginatedDisplayLines"
    [trackByFn]="trackDisplayLine"
    [clickableRows]="false"
    [currentPage]="currentPage"
    [itemsPerPage]="itemsPerPage"
    [enablePagination]="!showAllRows && displayLines.length > itemsPerPage"
    [showAllRowsActive]="showAllRows"
    [summaryOverride]="materialTableSummaryOverride"
    (showAll)="onMaterialShowAllRows()"
    (exitShowAll)="onMaterialExitShowAllRows()"
    (pageChange)="onMaterialPageChange($event)"
    [showSummary]="false"
    rowBaseClass="bg-background-tertiary"
    density="compact"
    variant="surface"
    [sourceTotal]="detailRows.length"
    [totalItems]="displayLines.length"
    summaryItemLabel="záznamů"
    [loading]="isLoading"
    loadingMessage="Načítání materiálů…"
    [errorMessage]="errorMessage || ''"
    (retry)="loadRows()"
    emptyTitle="Žádné materiály"
    emptyMessage="Žádné použité materiály v zvoleném období nebo neodpovídají filtru."
  >
    <ng-template appDataTableCell="usedAt" let-row="row">
      {{ formatDateTime(row.sourceRows[0].usedAt) }}
    </ng-template>

    <ng-template appDataTableCell="changedBy" let-row="row">
      {{ row.sourceRows[0].changedBy || '—' }}
    </ng-template>

    <ng-template appDataTableCell="supplier" let-row="row">
      {{ row.sourceRows[0].supplier || '—' }}
    </ng-template>

    <ng-template appDataTableCell="sapCode" let-row="row">
      {{ row.sapCode }}
    </ng-template>

    <ng-template appDataTableCell="name" let-row="row">
      <a
        *ngIf="row.sourceRows[0].objectId"
        [routerLink]="['/objects', row.sourceRows[0].objectId]"
        [queryParams]="objectDetailLinkQueryParams()"
        class="text-primary-600 dark:text-primary-400 hover:underline font-medium"
      >{{ row.name }}</a>
      <span *ngIf="!row.sourceRows[0].objectId" class="font-medium">{{ row.name }}</span>
      <p *ngIf="row.sourceRows[0].objectType" class="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
        {{ objectTypeLabel(row.sourceRows[0].objectType) }}
      </p>
    </ng-template>

    <ng-template appDataTableCell="quantity" let-row="row">
      {{ formatQuantity(row.quantity, row.unit) }}
    </ng-template>

    <ng-template appDataTableCell="price" let-row="row">
      {{ row.sourceRows[0].price != null ? formatPrice(row.sourceRows[0].price) : '—' }}
    </ng-template>

    <ng-template appDataTableCell="lineTotal" let-row="row">
      {{ formatPrice(row.lineTotal) }}
    </ng-template>

    <ng-template appDataTableCell="actions" let-row="row">
      <div *ngIf="canAdminEdit" class="flex items-center justify-end gap-2">
        <app-edit-icon-button
          ariaLabel="Upravit množství"
          title="Upravit množství"
          (onClick)="openEditRow(row.sourceRows[0])"
        ></app-edit-icon-button>
        <app-delete-icon-button
          *ngIf="canDeleteMaterialRow(row.sourceRows[0])"
          ariaLabel="Odstranit záznam"
          title="Odstranit záznam"
          (onClick)="deleteRow(row.sourceRows[0])"
        ></app-delete-icon-button>
      </div>
    </ng-template>
  </app-data-table>

  <app-data-table
    *ngIf="displayMode === 'aggregated'"
    [columns]="summaryTableColumns"
    [rows]="summaryTableRows"
    [trackByFn]="trackSummaryRow"
    [rowClassFn]="summaryRowClass"
    [clickableRows]="true"
    (rowClick)="onSummaryRowClick($event)"
    [enablePagination]="false"
    [enableShowAll]="false"
    [showSummary]="false"
    density="compact"
    variant="surface"
    tableClass="w-full text-sm tt-cat-tree"
    rowBaseClass=""
    [loading]="isLoading"
    loadingMessage="Načítání materiálů…"
    [errorMessage]="errorMessage || ''"
    (retry)="loadRows()"
    [summaryOverride]="materialTableSummaryOverride"
    [sourceTotal]="summaryTableRows.length"
    [totalItems]="summaryTableRows.length"
    emptyTitle="Žádné materiály"
    emptyMessage="Žádné použité materiály v zvoleném období nebo neodpovídají filtru."
  >
    <ng-template appDataTableCell="label" let-row="row">
      <span
        class="tt-cat-tree__label tt-cat-tree__label--narrow"
        [ngClass]="summaryLabelIndentClass(row)"
        [class.tt-cat-tree__label--category]="row.rowType === 'category'"
        [class.tt-cat-tree__label--leaf]="row.rowType === 'line'"
        [class.tt-cat-tree__label--total]="row.rowType === 'total'"
      >
        <span
          *ngIf="row.rowType === 'category'"
          class="tt-cat-tree__chevron"
          [class.tt-cat-tree__chevron--expanded]="isSummaryRowExpanded(row)"
          aria-hidden="true"
        ></span>
        <span *ngIf="row.rowType === 'line'" class="tt-cat-tree__spacer" aria-hidden="true"></span>
        <span class="tt-cat-tree__text">{{ row.label }}</span>
      </span>
    </ng-template>

    <ng-template appDataTableCell="sapCode" let-row="row">
      {{ row.rowType === 'line' ? row.sapCode : '' }}
    </ng-template>

    <ng-template appDataTableCell="plannedQuantity" let-row="row">
      {{ row.rowType === 'line' || row.rowType === 'category' ? formatSummaryPlannedQuantity(row) : '' }}
    </ng-template>

    <ng-template appDataTableCell="quantity" let-row="row">
      {{ row.rowType === 'line' || row.rowType === 'category' ? formatSummaryQuantity(row) : '' }}
    </ng-template>

    <ng-template appDataTableCell="lineTotal" let-row="row">
      <span [appCountUp]="formatPrice(row.lineTotal)" [appCountUpStatic]="row.rowType !== 'total'"></span>
    </ng-template>

    <ng-template appDataTableCell="variance" let-row="row">
      <span
        [ngClass]="summaryVarianceClass(row)"
        [appCountUp]="formatSummaryVariance(row)"
        [appCountUpStatic]="row.rowType !== 'total'"
      ></span>
    </ng-template>
  </app-data-table>

  <div *ngIf="displayMode === 'aggregated' && hasMaterialPlan && !isLoading && summaryTableRows.length > 0" class="flex flex-wrap items-center gap-4 text-xs text-neutral-600 dark:text-neutral-400 mt-2">
    <span class="inline-flex items-center gap-2 text-sky-800 dark:text-sky-200">
      <span class="inline-block w-8 h-3 rounded-sm bg-sky-100 dark:bg-sky-950/50 border border-sky-300 dark:border-sky-600" aria-hidden="true"></span>
      Plán (PPD)
    </span>
    <span class="inline-flex items-center gap-2 text-emerald-800 dark:text-emerald-200">
      <span class="inline-block w-8 h-3 rounded-sm bg-emerald-100 dark:bg-emerald-950/40 border border-emerald-400 dark:border-emerald-600" aria-hidden="true"></span>
      Skutečnost
    </span>
  </div>

  <p *ngIf="displayMode === 'aggregated' && !isLoading && summaryTableRows.length > 0" class="tt-cat-tree__hint">
    Klikněte na kategorii stromu materiálu pro rozbalení řádků SAP.
  </p>

  <div *ngIf="displayLines.length > 0 && !isLoading && !(displayMode === 'aggregated' && hasMaterialPlan)" class="mt-8 p-4 tt-surface-muted">
    <div class="flex justify-between items-center flex-wrap gap-3">
      <span class="text-lg font-semibold text-neutral-900 dark:text-neutral-100">{{ materialFooterLabel }}</span>
      <span class="text-xl font-bold text-primary-600 dark:text-primary-400" [appCountUp]="formatPrice(lineTotalSum)"></span>
    </div>
  </div>
</div>

<app-modal
  [isOpen]="!!editingRow"
  title="Upravit množství materiálu"
  [showFooter]="true"
  width="24rem"
  maxWidth="24rem"
  (closeEvent)="closeEditRow()">
  <div *ngIf="editingRow" class="space-y-4">
    <p class="text-sm text-neutral-700 dark:text-neutral-300">
      <span class="font-medium">{{ editingRow.name }}</span>
      <span class="text-neutral-500 dark:text-neutral-400"> ({{ editingRow.sapCode }})</span>
    </p>
    <p *ngIf="editingRow.changedBy && isPolygonScope" class="text-xs text-neutral-500 dark:text-neutral-400">
      Od: {{ editingRow.changedBy }}
    </p>
    <div>
      <label for="mp-edit-qty" class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Množství{{ editingRow.unit ? ' (' + editingRow.unit + ')' : '' }}</label>
      <input
        id="mp-edit-qty"
        type="text"
        inputmode="decimal"
        [(ngModel)]="editQtyStr"
        class="w-full rounded border border-neutral-300 dark:border-neutral-600 px-3 py-2 text-sm bg-background-tertiary"
      />
    </div>
    <p *ngIf="editError" class="text-sm text-red-600 dark:text-red-400">{{ editError }}</p>
  </div>
  <div modal-footer class="flex justify-end gap-2">
    <app-button variant="secondary" (onClick)="closeEditRow()" [disabled]="editSaving">Zrušit</app-button>
    <app-button variant="primary" (onClick)="saveEditRow()" [loading]="editSaving">Uložit</app-button>
  </div>
</app-modal>
